<script setup>
import { computed } from 'vue'
import dayjs from 'dayjs'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { useChips } from '@/composables/chips'

import ListItem from '@/components/shared/ListItem.vue'

const props = defineProps({
  movement: {
    type: Object,
    required: true,
  },
  scannable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['edit', 'confirm', 'cancel', 'delete', 'restore', 'scan'])

const i18n = getI18n()
const planningStore = usePlanningStore()
const { getWarehouseMovementTypeChips, getServiceTagChips } = useChips()

const { TRANSFER, ADJUSTMENT } = planningStore.warehouseMovementTypeEnum
const { SCHEDULED } = planningStore.warehouseMovementStatusEnum

// A confirmed movement is locked on desktop too: it can only be restored back to scheduled
// (revert) before it becomes editable/cancellable/deletable again.
const isPending = computed(() => props.movement.status === SCHEDULED)

const service = computed(() => planningStore.getServiceById(props.movement.service))
const label = computed(() => planningStore.getServiceName(service.value))
const avatar = computed(() => service.value?.avatar_file || service.value?.avatar_url)
const captionChips = computed(() => [
  ...getWarehouseMovementTypeChips(props.movement.movement_type),
  ...getServiceTagChips(service.value?.tags),
])

// Arrows always point "->", never "<-": one line for the source/warehouse side, one for the
// destination side (a linked project for check-in/check-out, the destination warehouse for a
// transfer). An adjustment only ever touches one warehouse, prefixed with "+" instead of an arrow.
const sourceLine = computed(() => `${props.movement.movement_type === ADJUSTMENT ? '+' : '→'} ${props.movement.warehouse_name}`)
const destinationLine = computed(() => {
  const m = props.movement
  if (m.movement_type === ADJUSTMENT) return null
  if (m.movement_type === TRANSFER) {
    const dateSuffix = m.destination_scheduled_at && !dayjs(m.destination_scheduled_at).isSame(m.scheduled_at, 'day')
      ? ` (${dayjs(m.destination_scheduled_at).format('DD/MM')})`
      : ''
    return `→ ${m.destination_warehouse_name}${dateSuffix}`
  }
  return m.project_name ? `→ ${m.project_name}` : null
})

const isProjectDestination = computed(() =>
  props.movement.movement_type !== ADJUSTMENT &&
  props.movement.movement_type !== TRANSFER &&
  !!props.movement.project_name
)
const projectColor = computed(() => planningStore.getProjectById(props.movement.project)?.color)
</script>
<template>
  <q-item
    class="q-pa-sm"
    :clickable="isPending"
    @click="isPending && emit(scannable ? 'scan' : 'edit', movement)"
  >
    <list-item
      :avatar="avatar"
      avatar-icon="mdi-camera-outline"
      :label="label"
      :caption-chips="captionChips"
      dense
    >
      <template #caption>
        <div class="ellipsis">{{ sourceLine }}</div>
        <div v-if="destinationLine" class="ellipsis">
          {{ destinationLine }}
          <q-icon
            v-if="isProjectDestination && projectColor"
            name="mdi-circle"
            :style="{ color: projectColor }"
            size="0.5rem"
            class="q-ml-xs"
          />
        </div>
      </template>
      <template #right>
        <q-item-section side class="row items-center no-wrap">
          <q-item-label class="q-mr-xs">{{ dayjs(movement.scheduled_at).format('DD/MM') }}</q-item-label>
          <template v-if="isPending">
            <q-btn
              v-if="scannable"
              flat
              round
              dense
              icon="mdi-qrcode-scan"
              color="primary"
              @click.stop="emit('scan', movement)"
            />
            <q-btn
              flat
              round
              dense
              icon="mdi-check"
              color="positive"
              @click.stop="emit('confirm', movement)"
            />
            <q-btn
              flat
              round
              dense
              icon="mdi-dots-vertical"
              @click.stop
            >
              <q-menu>
                <q-list>
                  <q-item
                    v-close-popup
                    clickable
                    @click="emit('edit', movement)"
                  >
                    <q-item-section avatar>
                      <q-icon name="mdi-pencil" color="info" />
                    </q-item-section>
                    <q-item-section>{{ i18n.t('common.edit') }}</q-item-section>
                  </q-item>
                  <q-item
                    v-if="!movement.is_from_confirmed_allocation"
                    v-close-popup
                    clickable
                    @click="emit('cancel', movement)"
                  >
                    <q-item-section avatar>
                      <q-icon name="mdi-close-octagon-outline" color="warning" />
                    </q-item-section>
                    <q-item-section>{{ i18n.t('common.cancel') }}</q-item-section>
                  </q-item>
                  <q-item
                    v-if="!movement.is_from_confirmed_allocation"
                    v-close-popup
                    clickable
                    @click="emit('delete', movement)"
                  >
                    <q-item-section avatar>
                      <q-icon name="mdi-delete" color="negative" />
                    </q-item-section>
                    <q-item-section>{{ i18n.t('common.delete') }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </template>
          <q-btn
            v-else
            flat
            round
            dense
            icon="mdi-undo-variant"
            color="grey-7"
            @click.stop="emit('restore', movement)"
          />
        </q-item-section>
      </template>
    </list-item>
  </q-item>
</template>
